import React from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl, intlShape } from 'react-intl';
import { get, includes, sortBy } from 'lodash';
import Table from './Table';
import { Tooltip, TooltipData } from '../elements-tooltip/tooltip';
import classnames from 'classnames';

const PriceTable = ({
  spaceRentalTotal,
  currency,
  processingFee,
  priceTotal,
  isHost,
  discounts = [],
  addOnsTotal,
  total,
  adjustmentsArray = [],
  // updateDifference only works for guest view at the moment
  updateDifference,
  hostAddOnsSummary = {},
  displayAddOns = true,
  currentDetails = false,
  bookingState = '',
  cancelledTotal = 0,
  cancelledRefund = 0,
  creditsApplied,
  remainingGuestTotal,
  currentBookingTotal = 0,
  splitPaymentFee = 0,
  splitPaymentAmount = 0,
  splitPaymentAmountWithoutFee = 0,
  splitPaymentTotal = 0,
  displaySplitPayments = false,
  bookingStartDate = '',
  bookingCreatedDate = '',
  masterContract,
  intl,
  splitPaymentChargeDate = ''
}) => {
  const tableHeaders = [
    { label: isHost
      ? intl.formatMessage({ id: 'inbox.price_table.payout', defaultMessage: 'Payout' })
      : intl.formatMessage({ id: 'inbox.price_table.price', defaultMessage: 'Price' })
    }
  ];
  const cancelledBooking = 'CANCELLED' === bookingState;
  const terminalBookingState = includes(['WITHDRAWN', 'DECLINED', 'EXPIRED'], bookingState);
  const currentPriceTotal = creditsApplied ? remainingGuestTotal : isHost ? priceTotal : cancelledBooking ? cancelledTotal : terminalBookingState ? 0 : priceTotal;

  const updateDifferenceDisplay = updateDifference ? creditsApplied ? remainingGuestTotal : updateDifference.refund || updateDifference.additional_payment || 0 : 0;
  const masterCalculations = masterContract?.calculations;

  const getSafeValue = (value, defaultValue = 0) => !isNaN(+value) ? value : defaultValue;

  const displayedSpaceRentalTotal = 
      !isHost && masterCalculations?.space_rental_guest_with_tax !== undefined
        ? getSafeValue(masterCalculations.space_rental_guest_with_tax)
        : getSafeValue(spaceRentalTotal);

  const displayedAddOnsTotal = isHost
      ? get(hostAddOnsSummary, 'amount', 0)
      : getSafeValue(masterCalculations?.add_ons_guest_with_tax, getSafeValue(masterCalculations?.add_ons_guest));

  const displayedSubtotal = isHost
    ? getSafeValue(masterCalculations?.subtotal_host)
    : getSafeValue(masterCalculations?.subtotal_guest_with_tax);

  return (
    <div className="PriceTable">
      <Table
        tableHeaders={tableHeaders}
        tableBody={
          (
            <div className="add-ons-rows">
              <div className="flex space-between table-row">
                <div className="title-field">
                  <FormattedMessage
                    id="inbox.price_table.space_total"
                    defaultMessage="Space rental total"
                  />
                </div>
                <div data-testing-id="space-rental-amount">
                  <FormattedNumber
                    value={displayedSpaceRentalTotal}
                    currency={currency}
                    style={'currency'}
                  />
                </div>
              </div>

              {
                discounts.map((discount, i) => {
                  // only display entitlement discounts to guests here
                  if ('DAY_RATE_DISCOUNT' !== discount.type && !isHost) {
                    const { value, title, max_amount, discount_str, apply_as, discount_amount } = discount;
                    const discountDescription = ('PERCENT_OFF' === apply_as)
                      ? <FormattedMessage id='inbox.price_table.summary_percent_off' defaultMessage='{value}% off' values={{ value: value }} />
                      : <FormattedMessage
                        id='inbox.price_table.summary_value_off'
                        defaultMessage='{value} off'
                        values={{
                          value: <FormattedNumber
                            value={value}
                            currency='USD'
                            style={'currency'}
                          />
                        }}
                      />;
                    const maxAmountDescripton = max_amount
                      ? <FormattedMessage
                        id='inbox.price_table.max_amount'
                        defaultMessage=', up to {amount}'
                        values={{
                          amount: <FormattedNumber
                            value={max_amount}
                            currency='USD'
                            style={'currency'}
                          />
                        }}
                      />
                      : '';
                    return (
                      <div className="flex space-between table-row discount-row" data-testing-id={`discount-row-${i}`}>
                        { discount_str ? <div data-testing-id={`discount-discount_str-${i}`}>{discount_str}</div>
                          : <div>
                            <div data-testing-id={`discount-title-${i}`}>{title}</div>
                            <div data-testing-id={`discount-description-${i}`}>({discountDescription}{maxAmountDescripton})</div>
                          </div>
                        }
                        <div data-testing-id={`discount-amount-${i}`}>
                          -<FormattedNumber value={discount_amount} currency={currency} style={'currency'} />
                        </div>
                      </div>
                    );
                  }

                  return null;
                })
              }

              {
                (!!displayAddOns && !!displayedAddOnsTotal)
                  && <div className="flex space-between table-row">
                    <div>Add-ons total</div>
                    <div data-testing-id="add-ons-total">
                      <FormattedNumber
                        value={displayedAddOnsTotal}
                        currency={currency}
                        style={'currency'}
                      />
                    </div>
                  </div>
              }

              { sortBy(adjustmentsArray, 'meta.info.category').map((adjustment, i) => {
                const adjustmentInfo = get(adjustment, 'meta.info', {});
                const displayName = 'OVERTIME' === adjustmentInfo.category
                  ? intl.formatMessage({ id: 'inbox.price_table.overtime', defaultMessage: 'Overtime' })
                  : 'DAMAGES' === adjustmentInfo.category
                    ? intl.formatMessage({ id: 'inbox.price_table.damages', defaultMessage: 'Damages' })
                    : '';
                  // only show overtime or damages for now
                if (displayName) {
                  return (
                    <div className="flex space-between table-row" data-testing-id={`adjustment-row-${i}`}>
                      <div className="title-field" data-testing-id={`adjustment-displayName-${i}`}>{displayName}</div>
                      <div data-testing-id={`adjustment-${i}`}>
                        <FormattedNumber value={adjustmentInfo.amount || 0} currency={currency} style={'currency'}/>
                      </div>
                    </div>
                  );
                }
              })
              }

              <div>
                <hr className="dotted margin-md-top margin-md-bottom" />
                <div className="flex space-between table-row">
                  <div className="title-field">
                    <FormattedMessage
                      id='inbox.price_table.subtotal'
                      defaultMessage='Subtotal'
                    />
                  </div>
                  <div data-testing-id="subtotal">
                    <FormattedNumber value={displayedSubtotal} currency={currency} style={'currency'} />
                  </div>
                </div>
              </div>

              <div className="margin-md-bottom">
                <div className="flex space-between">
                  <div className="title-field">
                    <span className="margin-md-right" data-testing-id="processing-title">
                      {isHost
                        ? <FormattedMessage id='inbox.price_table.fee' defaultMessage='Service Fee' />
                        : <FormattedMessage id='inbox.price_table.processing' defaultMessage='Processing' />
                      }
                    </span>
                    <Tooltip tooltip={TooltipData[isHost ? 'SERVICE_FEE' : 'PROCESSING_FEE']} />
                  </div>
                  <div data-testing-id="processing-amount">
                    { isHost ? '-' : ''}<FormattedNumber value={processingFee} currency={currency} style={'currency'} />
                  </div>
                </div>
              </div>

              { (!!terminalBookingState || !!cancelledBooking) && !isHost
                && <div>
                  <div className="flex space-between">
                    <div className="title-field">
                      <FormattedMessage
                        id='inbox.price_table.booking'
                        defaultMessage='Booking {booking}'
                        values={{
                          booking: bookingState.toLowerCase()
                        }}
                      />
                    </div>
                    <div data-testing-id="terminal-booking-state-amount">
                      -<FormattedNumber value={cancelledBooking ? cancelledRefund : priceTotal} currency={currency} style={'currency'} />
                    </div>
                  </div>
                </div>
              }

              { !!creditsApplied && !updateDifference
              && <div className="margin-md-bottom">
                <div className="flex space-between text-green">
                  <div className="title-field">
                    <span className="margin-md-right">
                      <FormattedMessage
                        id='inbox.price_table.credits'
                        defaultMessage='Credits'
                      />
                    </span>
                  </div>
                  <div data-testing-id="credits-no-difference-processing-amount">
                    -<FormattedNumber value={creditsApplied} currency={currency} style={'currency'} />
                  </div>
                </div>
              </div>
              }

              { (displaySplitPayments && !isHost && !!splitPaymentFee) && <div className="margin-md-bottom">
                <div className="flex space-between">
                  <div className="title-field">
                    <span className="margin-md-right">
                      <FormattedMessage
                        id='inbox.price_table.part_now_part_later_fee'
                        defaultMessage='Part now, part later fee'
                      />
                    </span>
                  </div>
                  <div data-testing-id="split-payment-fee">
                    <FormattedNumber value={splitPaymentFee} currency={currency} style={'currency'} />
                  </div>
                </div>
              </div>
              }

              <hr className="dotted margin-md-top margin-md-bottom" />

              { updateDifference
                ? <div>
                  <div className="flex space-between heavy table-row">
                    <div className="no-top-margin no-bottom-margin">
                      <FormattedMessage
                        id='inbox.price_table.total'
                        defaultMessage='Total'
                      />
                    </div>
                    <div data-testing-id="total">
                      <FormattedNumber value={priceTotal} currency={currency} style={'currency'} />
                    </div>
                  </div>

                  <div className="flex space-between table-row">
                    <div className="title-field">
                      <FormattedMessage
                        id='inbox.price_table.current_total'
                        defaultMessage='Current total'
                      />
                    </div>
                    <div data-testing-id="booking-subtotal">
                      <FormattedNumber value={currentBookingTotal} currency={currency} style={'currency'} />
                    </div>
                  </div>

                  { !!creditsApplied
                  && <div className="margin-md-bottom">
                    <div className="flex space-between text-green">
                      <div className="title-field">
                        <span className="margin-md-right">
                          <FormattedMessage
                            id='inbox.price_table.credits_title'
                            defaultMessage='Credits'
                          />
                        </span>
                      </div>
                      <div data-testing-id="booking-credits-applied">
                        -<FormattedNumber value={creditsApplied} currency={currency} style={'currency'} />
                      </div>
                    </div>
                  </div>
                  }

                  <div className="flex space-between heavy table-row">
                    <div className={classnames('h6 no-top-margin no-bottom-margin', { refund: updateDifference.refund })} data-testing-id="booking-update-difference-title">
                      { updateDifference.refund
                        ? <FormattedMessage id='inbox.price_table.refund' defaultMessage='Refund' />
                        : <FormattedMessage id='inbox.price_table.additional' defaultMessage='Additional Charge' />
                      }
                    </div>
                    <div className={classnames('h6 no-top-margin', { refund: updateDifference.refund })} data-testing-id="booking-update-difference-amount">
                      <FormattedNumber value={updateDifferenceDisplay} currency={currency} style={'currency'} />
                    </div>
                  </div>
                </div>

                : <div className="flex space-between heavy table-row total-row">
                  <div className="h6 no-top-margin no-bottom-margin flex flex-wrap" data-testing-id="total-receipt-title">
                    {currentDetails && !isHost
                      ? <FormattedMessage id="inbox.price_table.invoice" defaultMessage="Payment Receipt"/>
                      : ''
                    }
                    &nbsp;
                    <FormattedMessage id='inbox.price_table.total' defaultMessage='Total' />
                    &nbsp;
                    <div className="normal">
                      <FormattedMessage id='inbox.price_table.including_tax' defaultMessage='(including tax)' />
                    </div>
                  </div>
                  <div className={classnames('h6 no-top-margin', { 'no-bottom-margin': displaySplitPayments })} data-testing-id="total">
                    <FormattedNumber
                      value={displaySplitPayments && !isHost ? splitPaymentTotal : currentPriceTotal}
                      currency={currency}
                      style={'currency'}
                    />
                  </div>
                </div>
              }

              { (displaySplitPayments && !isHost) && <div className="split-payment-row" data-testing-id="split-payment-row">
                <div className="flex space-between table-row">
                  <div className="title-field">
                    <FormattedMessage
                      id='inbox.price_table.first_payment'
                      defaultMessage='First payment'
                    />
                    {' '}
                    {!!bookingCreatedDate && (
                      <span>
                        (<FormattedDate
                          value={bookingCreatedDate}
                          year="numeric"
                          month="short"
                          day="numeric"
                        />)
                      </span>
                    )}
                  </div>
                  <div data-testing-id="split-payment-due-now-amount">
                    <FormattedNumber
                      value={splitPaymentAmount}
                      currency={currency}
                      style={'currency'}
                    />
                  </div>
                </div>
                <div className="flex space-between table-row">
                  <div className="title-field" data-testing-id="split-payment-second-charge-date">
                    <FormattedMessage
                      id='inbox.price_table.second_payment'
                      defaultMessage='Second payment'
                    />
                    {' '}
                    (<FormattedDate
                      value={splitPaymentChargeDate}
                      year="numeric"
                      month="short"
                      day="numeric"
                    />)
                  </div>
                  <div data-testing-id="split-payment-second-charge-amount">
                    <FormattedNumber
                      value={splitPaymentAmountWithoutFee}
                      currency={currency}
                      style={'currency'}
                    />
                  </div>
                </div>
              </div> }
            </div>
          )
        }
      />
    </div>
  );
};

PriceTable.propTypes = {
  spaceRentalTotal: PropTypes.number,
  currency: PropTypes.string,
  processingFee: PropTypes.number,
  priceTotal: PropTypes.number,
  isHost: PropTypes.bool,
  discounts: PropTypes.array,
  addOnsTotal: PropTypes.number,
  total: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  adjustmentsArray: PropTypes.array,
  updateDifference: PropTypes.object,
  splitPaymentChargeDate: PropTypes.string,
  splitPaymentFee: PropTypes.number,
  splitPaymentAmount: PropTypes.number,
  splitPaymentAmountWithoutFee: PropTypes.number,
  splitPaymentTotal: PropTypes.number,
  displaySplitPayments: PropTypes.bool,
  bookingStartDate: PropTypes.node,
  bookingCreatedDate: PropTypes.string,
  intl: intlShape
};

export default injectIntl(PriceTable);
